import { Component, Output, EventEmitter, HostListener, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-export-document-button',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './export-document-button.html',
  styleUrls: ['./export-document-button.scss']
})
export class ExportDocumentButton implements OnInit, OnChanges {
  private _disabled: boolean = false;

  @Input()
  set disabled(value: boolean) {
    this._disabled = value;
  }
  get disabled(): boolean {
    return this._disabled;
  }

  // Nova propriedade para controlar se mostra escolha de tipo
  @Input() canChoose: boolean = false;

  // Input para meses disponíveis do backend (formato: "202512")
  @Input() availableMonths: string[] = [];

  // NOVOS INPUTS PARA PERÍODO INICIAL
  @Input() initialStartYear: number | null = null;
  @Input() initialStartMonth: number | null = null;
  @Input() initialEndYear: number | null = null;
  @Input() initialEndMonth: number | null = null;

  // Novo Output para exportação direta (quando canChoose é false)
  @Output() exportDirect = new EventEmitter<void>();

  // Outputs existentes
  @Output() exportRequested = new EventEmitter<{
    start: { year: number; month: number };
    end: { year: number; month: number }
  }>();

  @Output() exportCancelled = new EventEmitter<void>();

  isExportPickerOpen: boolean = false;
  isTypePickerOpen: boolean = false;
  selectedExportType: 'PDF' | 'EXCEL' | null = null;

  // Período inicial
  startYear: number = new Date().getFullYear();
  startMonth: number | null = null;

  // Período final
  endYear: number = new Date().getFullYear();
  endMonth: number | null = null;

  private originalStartYear: number = this.startYear;
  private originalStartMonth: number | null = this.startMonth;
  private originalEndYear: number = this.endYear;
  private originalEndMonth: number | null = this.endMonth;

  availableYears: number[] = [];
  monthNames: string[] = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  currentYear: number = new Date().getFullYear();
  currentMonth: number = new Date().getMonth() + 1;

  ngOnInit(): void {
    this.initializeYears();
    this.setDefaultPeriod();

    // ATUALIZAÇÃO: Se houver valores iniciais passados, use-os
    this.applyInitialValues();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['availableMonths']) {
      this.initializeYears();
    }

    // Detecta mudanças nos inputs de período inicial
    if (changes['initialStartYear'] || changes['initialStartMonth'] ||
        changes['initialEndYear'] || changes['initialEndMonth']) {

      // Se o picker não estiver aberto, aplica os novos valores
      if (!this.isExportPickerOpen) {
        this.applyInitialValues();
      }
    }
  }

  private applyInitialValues(): void {
    // Aplica os valores iniciais se foram fornecidos
    if (this.initialStartYear !== null && this.initialStartMonth !== null) {
      this.startYear = this.initialStartYear;
      this.startMonth = this.initialStartMonth;
    }

    if (this.initialEndYear !== null && this.initialEndMonth !== null) {
      this.endYear = this.initialEndYear;
      this.endMonth = this.initialEndMonth;
    }

    // Se apenas um período foi fornecido, usa o mesmo para ambos
    if (this.startMonth && !this.endMonth) {
      this.endYear = this.startYear;
      this.endMonth = this.startMonth;
    }

    if (this.endMonth && !this.startMonth) {
      this.startYear = this.endYear;
      this.startMonth = this.endMonth;
    }

    this.saveOriginalValues();
  }

  private setDefaultPeriod(): void {
    // Só aplica o período padrão se não houver valores iniciais
    if (this.initialStartYear === null && this.initialStartMonth === null &&
        this.initialEndYear === null && this.initialEndMonth === null) {

      // Define período padrão: mês atual como final, mês anterior como inicial
      this.endYear = this.currentYear;
      this.endMonth = this.currentMonth;

      if (this.currentMonth === 1) {
        // Se for janeiro, volta para dezembro do ano anterior
        this.startYear = this.currentYear - 1;
        this.startMonth = 12;
      } else {
        this.startYear = this.currentYear;
        this.startMonth = this.currentMonth - 1;
      }
    }

    this.saveOriginalValues();
  }

  private saveOriginalValues(): void {
    this.originalStartYear = this.startYear;
    this.originalStartMonth = this.startMonth;
    this.originalEndYear = this.endYear;
    this.originalEndMonth = this.endMonth;
  }

  private initializeYears(): void {
    const currentYear = new Date().getFullYear();
    this.availableYears = [];

    // Se há meses disponíveis do backend, extrai os anos
    if (this.availableMonths && this.availableMonths.length > 0) {
      const years = new Set<number>();
      this.availableMonths.forEach(month => {
        if (month && month.length >= 4) {
          const year = parseInt(month.substring(0, 4), 10);
          if (!isNaN(year)) {
            years.add(year);
          }
        }
      });
      this.availableYears = Array.from(years).sort((a, b) => a - b);
    } else {
      // Anos padrão: 2 anos atrás até ano atual
      for (let i = currentYear - 2; i <= currentYear; i++) {
        this.availableYears.push(i);
      }
    }
  }

  get availableMonthsArray(): string[] {
    return this.monthNames;
  }

  getShortMonthName(fullMonthName: string): string {
    const monthMap: { [key: string]: string } = {
      'Janeiro': 'Jan', 'Fevereiro': 'Fev', 'Março': 'Mar', 'Abril': 'Abr',
      'Maio': 'Mai', 'Junho': 'Jun', 'Julho': 'Jul', 'Agosto': 'Ago',
      'Setembro': 'Set', 'Outubro': 'Out', 'Novembro': 'Nov', 'Dezembro': 'Dez'
    };
    return monthMap[fullMonthName] || fullMonthName.substring(0, 3);
  }

  // NOVO MÉTODO: Retorna nome completo do mês
  getFullMonthName(fullMonthName: string): string {
    return fullMonthName;
  }

  // MÉTODO AUXILIAR: Retorna o nome apropriado do mês baseado no contexto
  getFormattedMonthName(monthIndex: number, showFullName: boolean = false): string {
    const monthName = this.monthNames[monthIndex - 1];
    return showFullName ? this.getFullMonthName(monthName) : this.getShortMonthName(monthName);
  }

  // VALIDAÇÃO: Verifica se o mês está disponível (se houver lista do backend)
  private isMonthAvailable(year: number, month: number): boolean {
    if (!this.availableMonths || this.availableMonths.length === 0) {
      return true; // Se não há lista, todos os meses estão disponíveis
    }

    const monthStr = month.toString().padStart(2, '0');
    const periodKey = `${year}${monthStr}`;
    return this.availableMonths.includes(periodKey);
  }

  // VALIDAÇÃO: Verifica se o período é futuro
  private isFutureMonth(year: number, month: number): boolean {
    const selectedDate = new Date(year, month - 1, 1);
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();
    const firstDayOfCurrentMonth = new Date(currentYear, currentMonth, 1);

    return selectedDate > firstDayOfCurrentMonth;
  }

  // MÉTODOS PARA O PERÍODO INICIAL
  isStartMonthActive(month: number): boolean {
    return this.startYear === this.startYear && this.startMonth === month;
  }

  isStartMonthDisabled(month: number): boolean {
    // 1. Verifica se o mês está disponível
    if (!this.isMonthAvailable(this.startYear, month)) {
      return true;
    }

    // 2. Verifica se é futuro
    if (this.isFutureMonth(this.startYear, month)) {
      return true;
    }

    // 3. Se já há um período final selecionado, verifica se é posterior
    if (this.endYear && this.endMonth) {
      const startDate = new Date(this.startYear, month - 1, 1);
      const endDate = new Date(this.endYear, this.endMonth - 1, 1);

      // Não pode ser posterior ao período final
      return startDate > endDate;
    }

    return false;
  }

  onStartYearChange(): void {
    // Se mudar o ano, limpa o mês se não estiver disponível
    if (this.startMonth && this.isStartMonthDisabled(this.startMonth)) {
      this.startMonth = null;
    }
  }

  selectStartMonth(month: number): void {
    if (this.disabled || this.isStartMonthDisabled(month)) {
      return;
    }

    this.startMonth = month;

    // Se o período final for anterior ao inicial, ajusta o final
    if (this.endYear && this.endMonth) {
      const startDate = new Date(this.startYear, month - 1, 1);
      const endDate = new Date(this.endYear, this.endMonth - 1, 1);

      if (startDate > endDate) {
        // Se inicial for posterior ao final, move o final para o mesmo mês/ano do inicial
        this.endYear = this.startYear;
        this.endMonth = month;
      }
    }
  }

  // MÉTODOS PARA O PERÍODO FINAL
  isEndMonthActive(month: number): boolean {
    return this.endYear === this.endYear && this.endMonth === month;
  }

  isEndMonthDisabled(month: number): boolean {
    // 1. Verifica se o mês está disponível
    if (!this.isMonthAvailable(this.endYear, month)) {
      return true;
    }

    // 2. Verifica se é futuro
    if (this.isFutureMonth(this.endYear, month)) {
      return true;
    }

    // 3. Se há um período inicial selecionado, verifica se é anterior
    if (this.startYear && this.startMonth) {
      const startDate = new Date(this.startYear, this.startMonth - 1, 1);
      const endDate = new Date(this.endYear, month - 1, 1);

      // Não pode ser anterior ao período inicial
      return endDate < startDate;
    }

    return false;
  }

  onEndYearChange(): void {
    // Se mudar o ano, limpa o mês se não estiver disponível
    if (this.endMonth && this.isEndMonthDisabled(this.endMonth)) {
      this.endMonth = null;
    }
  }

  selectEndMonth(month: number): void {
    if (this.disabled || this.isEndMonthDisabled(month)) {
      return;
    }

    this.endMonth = month;

    // Se o período inicial for posterior ao final, ajusta o inicial
    if (this.startYear && this.startMonth) {
      const startDate = new Date(this.startYear, this.startMonth - 1, 1);
      const endDate = new Date(this.endYear, month - 1, 1);

      if (startDate > endDate) {
        // Se final for anterior ao inicial, move o inicial para o mesmo mês/ano do final
        this.startYear = this.endYear;
        this.startMonth = month;
      }
    }
  }

  // VALIDAÇÃO DO PERÍODO COMPLETO
  isValidPeriod(): boolean {
    if (!this.startMonth || !this.endMonth) {
      return false;
    }

    const startDate = new Date(this.startYear, this.startMonth - 1, 1);
    const endDate = new Date(this.endYear, this.endMonth - 1, 1);

    return startDate <= endDate;
  }

  // FORMATAÇÃO ATUALIZADA - Mostra nome completo do mês quando é apenas um mês
  formatPeriodDisplay(): string {
    if (!this.startMonth || !this.endMonth) {
      return 'Selecione um período completo';
    }

    // Verifica se é o mesmo mês e ano
    const isSingleMonth = this.startYear === this.endYear && this.startMonth === this.endMonth;

    // Usa nome completo para mês único, abreviado para períodos
    const startMonthName = this.getFormattedMonthName(this.startMonth, isSingleMonth);
    const endMonthName = this.getFormattedMonthName(this.endMonth, isSingleMonth);

    if (isSingleMonth) {
      // Mostra apenas uma vez com nome completo: "Janeiro/2026"
      return `${startMonthName}/${this.startYear}`;
    } else if (this.startYear === this.endYear) {
      // Mesmo ano, meses diferentes: "Jan a Mar/2026" (nomes abreviados)
      return `${startMonthName} a ${endMonthName}/${this.startYear}`;
    } else {
      // Anos diferentes: "Jan/2026 a Mar/2027" (nomes abreviados)
      return `${startMonthName}/${this.startYear} a ${endMonthName}/${this.endYear}`;
    }
  }

  // CONTROLE DO BOTÃO E POPUPS
  handleButtonClick(): void {
    if (this.disabled) return;

    if (!this.canChoose) {
      // Se canChoose é false, chama a função diretamente
      this.exportDirect.emit();
    } else {
      // Se canChoose é true, abre o popup de escolha de tipo
      this.openTypePicker();
    }
  }

  openTypePicker(): void {
    if (this.disabled) return;

    // Fecha qualquer popup aberto
    if (this.isExportPickerOpen) {
      this.restoreOriginalValues();
      this.isExportPickerOpen = false;
    }

    this.isTypePickerOpen = !this.isTypePickerOpen;
    this.selectedExportType = null;
  }

  selectExportType(type: 'PDF' | 'EXCEL'): void {
    this.selectedExportType = type;

    if (type === 'PDF') {
      // Fecha os popups antes de emitir para que o DOM seja atualizado antes do parent gerar o PDF
      this.closeAllPopups();
      // Emite evento específico para PDF, para o pai abrir o modal
      setTimeout(() => this.exportPdfModal.emit(), 0);
    } else if (type === 'EXCEL') {
      // Para Excel, abre o popup de seleção de período
      this.openExportPicker();
    }
  }
  // Novo Output para exportação PDF com modal
  @Output() exportPdfModal = new EventEmitter<void>();

  openExportPicker(): void {
    if (this.disabled) return;

    // Fecha o popup de tipo
    this.isTypePickerOpen = false;

    if (!this.isExportPickerOpen) {
      // Ao abrir, verifica se há valores iniciais para aplicar
      this.applyInitialValues();
      this.saveOriginalValues();
    } else {
      // Se estiver fechando sem confirmar, restaura valores originais
      this.restoreOriginalValues();
    }

    this.isExportPickerOpen = !this.isExportPickerOpen;
  }

  private restoreOriginalValues(): void {
    this.startYear = this.originalStartYear;
    this.startMonth = this.originalStartMonth;
    this.endYear = this.originalEndYear;
    this.endMonth = this.originalEndMonth;
  }

  closeAllPopups(): void {
    this.isTypePickerOpen = false;
    this.isExportPickerOpen = false;
    this.selectedExportType = null;
  }

  cancelExport(): void {
    this.restoreOriginalValues();
    this.closeAllPopups();
    this.exportCancelled.emit();
  }

  confirmExport(): void {
    if (!this.isValidPeriod()) {
      return;
    }

    this.isExportPickerOpen = false;
    this.isTypePickerOpen = false;
    this.saveOriginalValues(); // Salva como novos valores originais

    if (this.selectedExportType === 'EXCEL') {
      // Emite evento específico para Excel exportar e abrir modal no pai
      this.exportExcelModal.emit({
        start: {
          year: this.startYear,
          month: this.startMonth!
        },
        end: {
          year: this.endYear,
          month: this.endMonth!
        }
      });
    } else {
      this.exportRequested.emit({
        start: {
          year: this.startYear,
          month: this.startMonth!
        },
        end: {
          year: this.endYear,
          month: this.endMonth!
        }
      });
    }
  }
  // Novo Output para exportação Excel com modal
  @Output() exportExcelModal = new EventEmitter<{
    start: { year: number; month: number };
    end: { year: number; month: number };
  }>();

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const isInsideTypePicker = target.closest('.type-picker-selector');
    const isInsideExportPicker = target.closest('.export-selector');
    const isInsideExportButton = target.closest('.export-btn');

    if (!isInsideTypePicker && !isInsideExportPicker && !isInsideExportButton) {
      if (this.isTypePickerOpen || this.isExportPickerOpen) {
        this.restoreOriginalValues();
        this.closeAllPopups();
        this.exportCancelled.emit();
      }
    }
  }
}
